import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { getLocalStorageItem } from "utils/functions";
import { getMemberSettings } from "utils/accessLocalStorageData";
import appScreenshot1 from "assets/images/app-screenshot-1.png";
import appScreenshot2 from "assets/images/app-screenshot-2.png";

// Icons
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Fonts, FontColors } from "theme/fontConfig";

// Same tombstone icon as sidebar
const YahrzeitIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 22V13a5 5 0 0 1 10 0V22" />
    <line x1="4" y1="22" x2="20" y2="22" />
    <polygon points="12,14 13.7,17 10.3,17" />
    <polygon points="12,18 13.7,15 10.3,15" />
  </svg>
);

const MODULE_COLORS = {
  members:   { bg: "#EEF2FF", icon: "#2555EF", border: "#C7D2FE" },
  yahrzeit:  { bg: "#FFF7ED", icon: "#EA580C", border: "#FED7AA" },
  education: { bg: "#F0FDF4", icon: "#16A34A", border: "#BBF7D0" },
  billing:   { bg: "#FFF1F2", icon: "#E11D48", border: "#FECDD3" },
  events:    { bg: "#F0F9FF", icon: "#0284C7", border: "#BAE6FD" },
  donations: { bg: "#FDF4FF", icon: "#9333EA", border: "#E9D5FF" },
  directory: { bg: "#FFFBEB", icon: "#D97706", border: "#FDE68A" },
  settings:  { bg: "#F8FAFC", icon: "#475569", border: "#E2E8F0" },
};

const ModuleCard = ({ title, description, icon: Icon, colors, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      background: "#ffffff",
      border: "1px solid #E2E8F0",
      borderRadius: "14px",
      p: { xs: 1.5, sm: 2.5 },
      cursor: "pointer",
      width: "100%",
      transition: "all 0.18s ease",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      "&:hover": {
        borderColor: colors.border,
        boxShadow: "0 4px 16px rgba(0,0,0,0.07)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "10px",
        bgcolor: colors.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: colors.icon,
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 22 }} />
    </Box>
    <Box>
      <Typography
        sx={{
          ...Fonts.GeneralSmallHeaderFont,
          color: FontColors.Slate0F172A,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: Fonts.GeneralCaptionFont.fontFamily,
          fontSize: Fonts.GeneralCaptionFont.fontSize,
          color: FontColors.Slate94A3B8,
          lineHeight: 1.5,
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const HomePage = () => {
  const history = useHistory();
  const user = getLocalStorageItem("accountInfo", true);
  const logo = getLocalStorageItem("getOrginasitionBySubDomain", true);
  const memberSettings = getMemberSettings();
  const memberPortalDirectory = JSON.parse(localStorage.getItem("memberPortalDirectory"));
  const { logoImage } = useSelector((state) => state.organization);

  const firstName = user?.firstName || user?.nickname?.split(" ")?.[0] || "there";

  const modules = [
    {
      key: "members",
      title: "Family Members",
      description: "View and manage your family members and household details.",
      icon: PeopleAltOutlinedIcon,
      url: "/family-member-table",
      enabled: memberSettings?.enableMembers,
    },
    {
      key: "yahrzeit",
      title: "Yahrzeit",
      description: "Manage yahrzeit remembrances and upcoming observances.",
      icon: YahrzeitIcon,
      url: "/yahrzeit",
      enabled: memberSettings?.enableYahrzeit,
    },
    {
      key: "education",
      title: "Education",
      description: "Enroll in classes and manage your educational programs.",
      icon: SchoolOutlinedIcon,
      url: "/classes",
      enabled: memberSettings?.enableEducation,
    },
    {
      key: "billing",
      title: "Billing",
      description: "Review your charges, make payments, and download statements.",
      icon: AttachMoneyOutlinedIcon,
      url: "/finance",
      enabled: memberSettings?.enableBilling,
    },
    {
      key: "events",
      title: "Events",
      description: "Browse and register for upcoming community events.",
      icon: EventOutlinedIcon,
      url: "/events-view",
      enabled: memberSettings?.enableEvents,
    },
    {
      key: "donations",
      title: "Donations",
      description: "Make a donation and view your giving history.",
      icon: VolunteerActivismOutlinedIcon,
      url: "/donation-view",
      enabled: memberSettings?.enableDonations,
    },
    {
      key: "directory",
      title: "Directory",
      description: "Browse the community member directory.",
      icon: MenuBookOutlinedIcon,
      url: "/directory",
      enabled: memberPortalDirectory,
    },
    {
      key: "settings",
      title: "Settings",
      description: "Update your personal information and payment methods.",
      icon: SettingsOutlinedIcon,
      url: "/settings",
      enabled: memberSettings?.enableSettings,
    },
  ].filter((m) => m.enabled);

  return (
    <Box>
      {/* Welcome banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2555EF 0%, #1a3db5 100%)",
          borderRadius: { xs: "12px", md: "16px" },
          p: { xs: 2.5, md: 4 },
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Decorative circle */}
        <Box
          sx={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.04)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: 60,
            bottom: -60,
            width: 150,
            height: 150,
            borderRadius: "50%",
            bgcolor: "rgba(37,85,239,0.15)",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            sx={{
              ...Fonts.MembersPageHeaderFont,
              color: FontColors.White,
              mb: 0.5,
            }}
          >
            Welcome back, {firstName} 👋
          </Typography>
          <Typography
            sx={{
              fontFamily: Fonts.GeneralBodyFont.fontFamily,
              fontSize: Fonts.GeneralBodyFont.fontSize,
              color: FontColors.WhiteAlpha65,
            }}
          >
            Here's a quick overview of your member portal.
          </Typography>
        </Box>
      </Box>

      {/* Announcement banner */}
      {memberSettings?.announcement && (
        <Box
          sx={{
            background: "#F0F4FF",
            border: "1px solid #C7D7FD",
            borderRadius: "12px",
            p: { xs: 2, md: 2.5 },
            mb: 3,
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ fontSize: Fonts.GeneralXLargeBodyFont.fontSize, lineHeight: 1, mt: "2px" }}>📢</Box>
          <Typography
            sx={{
              fontFamily: Fonts.GeneralBodyFont.fontFamily,
              fontSize: Fonts.GeneralBodyFont.fontSize,
              color: FontColors.Blue1E3A8A,
              lineHeight: 1.7,
            }}
            dangerouslySetInnerHTML={{ __html: memberSettings.announcement }}
          />
        </Box>
      )}

      {/* Section title */}
      <Typography
        sx={{
          ...Fonts.GeneralSubHeaderFont,
          color: FontColors.Slate0F172A,
          mb: 2,
        }}
      >
        Quick Access
      </Typography>

      {/* Module grid */}
      <Grid container spacing={2}>
        {modules.map((mod) => (
          <Grid item xs={6} sm={6} md={4} lg={3} key={mod.key} sx={{ display: "flex" }}>
            <ModuleCard
              title={mod.title}
              description={mod.description}
              icon={mod.icon}
              colors={MODULE_COLORS[mod.key]}
              onClick={() => history.push(mod.url)}
            />
          </Grid>
        ))}
      </Grid>

      {/* App download banner */}
      {logo?.memberPortalApp && (
        <Box
          sx={{
            mt: 3,
            background: "linear-gradient(135deg, #ddeeff 0%, #eaf2ff 50%, #f0f6ff 100%)",
            borderRadius: "20px",
            px: { xs: 3, lg: 5 },
            pt: { xs: 3, lg: 3.5 },
            pb: { xs: 3, lg: 0 },
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "flex-start", lg: "flex-end" },
            justifyContent: "space-between",
            gap: { xs: 2, lg: 4 },
            position: "relative",
            overflow: "hidden",
            border: "1px solid rgba(37,85,239,0.1)",
            minHeight: { lg: 260 },
          }}
        >
          {/* Decorative circles */}
          <Box sx={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", bgcolor: "rgba(37,85,239,0.06)" }} />
          <Box sx={{ position: "absolute", right: 80, bottom: -60, width: 150, height: 150, borderRadius: "50%", bgcolor: "rgba(37,85,239,0.04)" }} />

          {/* Left: text + badges */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, position: "relative", zIndex: 1, pb: { xs: 0, lg: 3.5 } }}>
            {/* Now Live pill */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "100px",
                px: 1.5,
                py: 0.5,
                width: "fit-content",
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#22c55e", flexShrink: 0 }} />
              <Typography sx={{ ...Fonts.GeneralSmallLabelFont, color: FontColors.Slate0F172A, letterSpacing: "0.04em" }}>
                NOW LIVE
              </Typography>
            </Box>

            {/* Headline */}
            <Box>
              <Typography sx={{ ...Fonts.MembersPageHeadlineFont, color: FontColors.Slate0F172A, lineHeight: 1.2, mb: 1 }}>
                EasyShul{" "}
                <Box component="span" sx={{ color: FontColors.Blue2555EF }}>Members App</Box>
                {" "}is Now Live
              </Typography>
              <Typography sx={{ fontFamily: Fonts.GeneralBodyFont.fontFamily, fontSize: Fonts.GeneralBodyFont.fontSize, color: FontColors.Slate64748B, lineHeight: 1.6 }}>
                Access your member portal on the go — anytime, anywhere.
              </Typography>
            </Box>

            {/* Badges — always side by side, never wrap */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
              <a href="https://apps.apple.com/il/app/easyshul-members/id6758013315" target="_blank" rel="noopener noreferrer" style={{ display: "flex", flexShrink: 0 }}>
                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" height={36} style={{ borderRadius: 8 }} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.members.easyshul" target="_blank" rel="noopener noreferrer" style={{ display: "flex", flexShrink: 0 }}>
                <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Get it on Google Play" height={46} />
              </a>
            </Box>
          </Box>

          {/* Right: two phone screenshots — sit on bottom edge, desktop only */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "flex-end",
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
              gap: 1.5,
            }}
          >
            <img
              src={appScreenshot1}
              alt="EasyShul app home screen"
              style={{ height: 260, width: "auto", display: "block" }}
            />
            <img
              src={appScreenshot2}
              alt="EasyShul app account screen"
              style={{ height: 260, width: "auto", display: "block" }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HomePage;
